﻿import { defineStore } from 'pinia';
import { store } from '@/app/stores';
import { ModalState } from '@/app/types/modals';
import { type LocaleCode } from '@/app/lang/locales';
import type { I18n } from 'vue-i18n';
import { BGM_BOSS_SRC, BGM_SRC, type BgmModule, type BgmScene } from '@/app/utils/sound';

const LOCALE_STORAGE_KEY = 'app-locale';

let i18nInstance: I18n | null = null;

export function setI18nInstance(i18n: I18n) {
    i18nInstance = i18n;
}

const SOUND_STORAGE_KEY = 'sound';
const BGM_SOUND_STORAGE_KEY = 'bgm-sound';

function parseStorage(key: string): unknown {
    try {
        return JSON.parse(localStorage.getItem(key) ?? 'null');
    } catch {
        return null;
    }
}

function readLegacyToggle(value: unknown): boolean | null {
    if (typeof value === 'boolean') return value;
    if (value && typeof value === 'object') {
        const values = Object.values(value as Record<string, unknown>);
        const firstBoolean = values.find((item): item is boolean => typeof item === 'boolean');
        return firstBoolean ?? null;
    }
    return null;
}

function loadSound(): boolean {
    return readLegacyToggle(parseStorage(SOUND_STORAGE_KEY)) ?? false;
}

function loadBgmSound(): boolean {
    return readLegacyToggle(parseStorage(BGM_SOUND_STORAGE_KEY)) ?? loadSound();
}

const BGM_TRACK_STORAGE_KEY = 'bgm-track';
const BGM_BOSS_TRACK_STORAGE_KEY = 'bgm-boss-track';
// Default track per module/page scene
const DEFAULT_BGM_TRACK: Record<BgmModule, Record<BgmScene, string>> = {
    typing: {
        intro: 'winter',
        inplay: 'arcade',
    },
};
const DEFAULT_BGM_BOSS_TRACK: Record<BgmModule, string> = {
    typing: 'bossGrind',
};

function isValidBgmTrack(module: BgmModule, track: unknown): track is string {
    return typeof track === 'string' && track in BGM_SRC[module];
}

// Load stored BGM tracks per module/page scene and fall back when the track no longer exists.
function loadBgmTracks(): Record<BgmModule, Record<BgmScene, string>> {
    let saved: Partial<Record<BgmModule, string | Partial<Record<BgmScene, string>>>> = {};
    try {
        saved = JSON.parse(localStorage.getItem(BGM_TRACK_STORAGE_KEY) ?? '{}');
    } catch {
        saved = {};
    }
    return Object.fromEntries(
        (Object.keys(BGM_SRC) as BgmModule[]).map((m) => {
            const moduleSaved = saved[m];
            const tracks = Object.fromEntries(
                (Object.keys(DEFAULT_BGM_TRACK[m]) as BgmScene[]).map((scene) => {
                    const savedTrack =
                        typeof moduleSaved === 'object' && moduleSaved !== null ? moduleSaved[scene] : moduleSaved;
                    return [scene, isValidBgmTrack(m, savedTrack) ? savedTrack : DEFAULT_BGM_TRACK[m][scene]];
                }),
            ) as Record<BgmScene, string>;
            return [m, tracks];
        }),
    ) as Record<BgmModule, Record<BgmScene, string>>;
}

function loadBgmBossTracks(): Record<BgmModule, string> {
    let saved: Partial<Record<BgmModule, string>> = {};
    try {
        saved = JSON.parse(localStorage.getItem(BGM_BOSS_TRACK_STORAGE_KEY) ?? '{}');
    } catch {
        saved = {};
    }
    return Object.fromEntries(
        (Object.keys(BGM_SRC) as BgmModule[]).map((m) => {
            const t = saved[m];
            return [m, t && t in BGM_BOSS_SRC[m] ? t : DEFAULT_BGM_BOSS_TRACK[m]];
        }),
    ) as Record<BgmModule, string>;
}

interface SystemState {
    modalState: ModalState;
    isSlowNetwork: boolean;
    pingTime: number;
    locale: LocaleCode;
    sound: boolean;
    bgmSound: boolean;
    bgmTrack: Record<BgmModule, Record<BgmScene, string>>;
    bgmBossTrack: Record<BgmModule, string>;
}

export const useSystemStore = defineStore('app-system', {
    state: (): SystemState => ({
        modalState: {
            show: false,
            title: '',
            message: '',
            confirmEvent: null,
        },
        isSlowNetwork: false,
        pingTime: 0,
        locale: (localStorage.getItem(LOCALE_STORAGE_KEY) as LocaleCode) || 'ko',
        sound: loadSound(),
        bgmSound: loadBgmSound(),
        bgmTrack: loadBgmTracks(),
        bgmBossTrack: loadBgmBossTracks(),
    }),
    getters: {
        getModalState(): ModalState {
            return this.modalState;
        },
        getIsSlowNetwork(): boolean {
            return this.isSlowNetwork;
        },
        getPingTime(): number {
            return this.pingTime;
        },
        getSound() {
            return (): boolean => this.sound;
        },
        getBgmSound() {
            return (): boolean => this.bgmSound;
        },
        getBgmTrack() {
            return (module: BgmModule, scene: BgmScene = 'inplay'): string => this.bgmTrack[module][scene];
        },
        getBgmBossTrack() {
            return (module: BgmModule): string => this.bgmBossTrack[module];
        },
    },
    actions: {
        setIsSlowNetwork(isSlowNetwork: boolean) {
            this.isSlowNetwork = isSlowNetwork;
        },
        setPingTime(pingTime: number) {
            this.pingTime = pingTime;
        },
        setModalState(modalState: ModalState) {
            this.modalState = modalState;
        },
        hideModalState() {
            this.modalState.show = false;
            this.modalState.title = '';
            this.modalState.message = '';
            this.modalState.confirmEvent = null;
        },
        changeLocale(code: LocaleCode) {
            this.locale = code;
            localStorage.setItem(LOCALE_STORAGE_KEY, code);
            this.setLangClass();
            if (i18nInstance) {
                (i18nInstance.global.locale as { value: LocaleCode }).value = code;
            }
        },
        setLangClass() {
            document.body.classList.remove('lang-ko', 'lang-en', 'lang-vi', 'lang-zh', 'lang-th');
            document.body.classList.add(`lang-${this.locale}`);
        },
        hasTranslation() {
            return this.locale !== 'ko';
        },
        setSound() {
            this.sound = !this.sound;
            localStorage.setItem(SOUND_STORAGE_KEY, JSON.stringify(this.sound));
        },
        setBgmSound() {
            this.bgmSound = !this.bgmSound;
            localStorage.setItem(BGM_SOUND_STORAGE_KEY, JSON.stringify(this.bgmSound));
        },
        setBgmTrack(module: BgmModule, scene: BgmScene, track: string) {
            this.bgmTrack[module][scene] = track;
            localStorage.setItem(BGM_TRACK_STORAGE_KEY, JSON.stringify(this.bgmTrack));
        },
        setBgmBossTrack(module: BgmModule, track: string) {
            this.bgmBossTrack[module] = track;
            localStorage.setItem(BGM_BOSS_TRACK_STORAGE_KEY, JSON.stringify(this.bgmBossTrack));
        },
    },
});

export function useSystemStoreWithOut() {
    return useSystemStore(store);
}
